import { createContext, useContext } from 'react';

import type {
	DcrAddressInput,
	DcrLicenseInput,
	DcrParentHcoInput,
	EntityType
} from '@/core/graphql/generated/types';
import type { usePicklistValues } from '@/features/DataChangeRequest/_hooks/usePicklistValues';

export type Picklists = ReturnType<typeof usePicklistValues>['picklists'];

export type ArrayPicklistKey = Exclude<
	keyof Picklists,
	'stateOptionsDictionary'
>;

export type InlineEditEntity = {
	vid_V: string | null | undefined;
	id: string | null | undefined;
	countryId: string | null | undefined;
	statusCda_VId: string | null | undefined;
};

export type DraftFieldChange = {
	fieldName: string;
	inputType: 'text' | 'phone' | 'picklist' | 'multiPicklist' | 'boolean';
	value: string | boolean | Array<{ id?: string; name?: string }> | null;
	displayValue?: string | null;
	picklistKey?: string;
	isNewField?: boolean;
};

export type SubmitFieldArgs = {
	change: DraftFieldChange;
	notes?: string;
	thirdPartyConfirmed: boolean;
	attachmentIds?: string[];
};

export type SubmitFieldsArgs = {
	changes: DraftFieldChange[];
	notes?: string;
	thirdPartyConfirmed: boolean;
	attachmentIds?: string[];
};

export type SubmitAddressArgs = {
	addressInput: DcrAddressInput;
	notes?: string;
	thirdPartyConfirmed: boolean;
	attachmentIds?: string[];
};

export type SubmitLicenseArgs = {
	licenseInput: DcrLicenseInput;
	notes?: string;
	thirdPartyConfirmed: boolean;
	attachmentIds?: string[];
};

export type SubmitAffiliationArgs = {
	affiliationInputs: DcrParentHcoInput[];
	notes?: string;
	thirdPartyConfirmed: boolean;
	attachmentIds?: string[];
};

export type AddEntityKind = 'address' | 'license' | 'affiliation';

export type CandidateField = {
	fieldName: string;
	label: string;
	isPresent: boolean;
	entityKind?: AddEntityKind;
};

export type SectionRegistration = {
	key: string;
	label: string;
	fields: CandidateField[];
};

export type InlineEditContextValue = {
	enabled: boolean;
	entity: InlineEditEntity | null;
	entityType: EntityType;
	picklists: Partial<Picklists>;
	unavailableFields: string[];
	activeFieldName: string | null;
	pendingActivation: string | null;
	requestActivate: (fieldName: string) => void;
	requestDeactivate: (fieldName: string) => void;
	requestCloseActive: () => void;
	setActiveDirty: (isDirty: boolean) => void;
	getActiveDirty: () => boolean;
	confirmPendingActivation: () => void;
	cancelPendingActivation: () => void;
	submitField: (
		args: SubmitFieldArgs
	) => Promise<{ ok: true } | { ok: false; error: string }>;
	submitFields: (
		args: SubmitFieldsArgs
	) => Promise<{ ok: true } | { ok: false; error: string }>;
	submitAddress: (
		args: SubmitAddressArgs
	) => Promise<{ ok: true } | { ok: false; error: string }>;
	submitLicense: (
		args: SubmitLicenseArgs
	) => Promise<{ ok: true } | { ok: false; error: string }>;
	submitAffiliation: (
		args: SubmitAffiliationArgs
	) => Promise<{ ok: true } | { ok: false; error: string }>;
	registerAddedField: (fieldName: string) => void;
	deregisterAddedField: (fieldName: string) => void;
	addedFieldNames: Record<string, true>;
	sections: Record<string, SectionRegistration>;
	sectionOrder: string[];
	registerSection: (registration: SectionRegistration) => void;
	unregisterSection: (key: string) => void;
	requestAddEntityModal: (kind: AddEntityKind) => void;
};

export const InlineEditContext = createContext<InlineEditContextValue | null>(
	null
);

export const useInlineEditContext = () => useContext(InlineEditContext);
